import CheckmarkIcon from "@mui/icons-material/CheckCircle";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { RootState } from "../../../../core/store/store";

type ImageData = {
  img: string;
  title: string;
  author: string;
};

interface CreativeCommonsTabProps {
  onSelectImage: (url: string, attribution?: string) => void;
  defaultSearch?: string;
}

export const CreativeCommonsTab: React.FC<CreativeCommonsTabProps> = ({ 
  onSelectImage,
  defaultSearch = ""
}) => {
  const [search, setSearch] = useState(defaultSearch);
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState<ImageData[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [attributionEnabled, setAttributionEnabled] = useState(true);
  const { apiUrl } = useSelector((state: RootState) => state.home);
  const theme = useTheme();

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!search) return;

    setLoading(true);
    try {
      const response = await axios.post(`${apiUrl}/article/search-images`, {
        library: "Creative Commons",
        search,
      });
      const result = response.data.result as ImageData[];
      setImages(result.slice(0, 30));
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || "Failed to search images");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectImage = (image: ImageData) => {
    setSelected(image.img);
    const attribution = attributionEnabled
      ? `<p style="font-size: 8px; color: gray;"><i>"${image.title}" from <a href="${image.img}" target="_blank">${image.author}</a> and used with no modifications.</i></p>`
      : "";
    onSelectImage(image.img, attribution);
  };

  return (
    <Stack spacing={3}>
      <form onSubmit={handleSearch}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Search for images that are free to use with proper attribution, under a Creative Commons license.
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              variant="outlined"
              placeholder="Enter a keyword here..."
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <LoadingButton
              variant="contained"
              type="submit"
              disabled={!search || loading}
              loading={loading}
              startIcon={!loading && <SearchIcon />}
            >
              Search
            </LoadingButton>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={attributionEnabled}
                onChange={(e) => setAttributionEnabled(e.target.checked)}
              />
            }
            label="Automatically Insert Creative Commons Attribution"
          />
        </Stack>
      </form>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : images.length > 0 ? (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click on an image to select it for insertion
          </Typography>
          <ImageList variant="masonry" cols={3} gap={12}>
            {images.map((item) => (
              <ImageListItem
                key={item.img}
                onClick={() => handleSelectImage(item)}
                sx={{
                  cursor: "pointer",
                  border: selected === item.img ? `2px solid ${theme.palette.primary.main}` : "2px solid transparent",
                  borderRadius: 1,
                  overflow: "hidden",
                  transition: "all 0.2s",
                  "&:hover": {
                    transform: "scale(1.02)",
                    boxShadow: theme.shadows[4],
                  },
                }}
              >
                <img
                  src={item.img}
                  alt={item.title}
                  loading="lazy"
                  style={{ borderRadius: 4 }}
                />
                <ImageListItemBar
                  title={item.title}
                  subtitle={item.author}
                  position="bottom"
                />
                {selected === item.img && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      bgcolor: theme.palette.primary.main,
                      borderRadius: "50%",
                      width: 32,
                      height: 32,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CheckmarkIcon sx={{ color: "white", fontSize: 20 }} />
                  </Box>
                )}
              </ImageListItem>
            ))}
          </ImageList>
        </Box>
      ) : (
        <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
          {search ? "No images found. Try different keywords." : "Enter keywords to search for images"}
        </Box>
      )}
    </Stack>
  );
};